/**
 * The three-column ledger — `design_handoff_order_and_lottery` §"Screens".
 *
 * Present in every state; only how much of it is filled changes. Everything it decides is in
 * `domain/orderLedger.ts` — this file is geometry and nothing else, which is what lets the six
 * states be asserted without a browser.
 *
 * WHY AN UNDRAWN ROW IS A SHAPE AND NOT A BLANK. Mid-draw the ledger maps the drawn order, so the
 * name in an unfilled row IS the result. It renders as a dashed circle and a `#f2f4f8` bar: a
 * blank row reads as a bug, and a name reads as the answer arriving early.
 */

import { bothZones } from '../domain/deadlines.js';
import { managerShort, teamOfGm } from '../domain/identity.js';
import type { Head, LedgerRow } from '../domain/orderLedger.js';
import { ordinal } from '../domain/lotteryReveal.js';
import { GmBadge } from './GmBadge.js';

export function OrderLedger(
  { heads, rows, caption }: { heads: [Head, Head, Head]; rows: LedgerRow[]; caption?: string },
) {
  return (
    <section className="dolledger" aria-label={caption ?? 'Choosing order and draft slots'}>
      <div className="dolledgerhead" role="row">
        {heads.map((h) => (
          <span key={h.label} className="dolledgercol" role="columnheader">
            <b>{h.label}</b>
            <i className={`dolsub ${h.tone}`}>{h.sub}</i>
          </span>
        ))}
      </div>

      <ol className="dolledgerbody">
        {rows.map((r) => (
          <li key={r.position}
            className={`dolledgerrow${r.mine ? ' mine' : ''}${r.drawn ? '' : ' undrawn'}`}>
            {/*
              THE RANK, SET AS A RANK.

              Three drafts got here. A bare numeral in a circle, adrift in a 184px lane. Then an
              ordinal crammed into the same circle — which stopped being a circle, because "1st"
              and "10th" are different widths, so the column became a row of ragged lozenges
              fighting the GM avatar two inches to its right for the reader's attention.

              What it needed was less. The numeral carries itself: 18px, tabular, the digits
              right-aligned in a fixed box so 1 through 10 line up down the column and the suffix
              starts at the same x on every row. No container, no border — one bounding shape per
              row is enough, and it belongs to the man's face, not to his position. The viewer's
              own row goes blue and heavier; the row tint behind it does the rest.

              An undrawn row takes the same grey bar the empty slot cell takes, rather than a
              dashed ring of its own: the page already has one way of saying "not yet".
            */}
            {r.drawn ? (
              <span className={`dolpos${r.mine ? ' mine' : ''}`}>
                <b>{r.position}</b><i>{ordinal(r.position)}</i>
              </span>
            ) : (
              <span className="dolposhole" aria-hidden="true" />
            )}

            {r.gm ? (
              <span className="dolwho">
                <GmBadge gm={r.gm} />
                <span className="dolnames">
                  <b>{managerShort(r.gm)}</b>
                  <i>{teamOfGm(r.gm)}</i>
                </span>
                <SubmittedMark row={r} />
                {r.tag && <span className={`doltag ${r.tagTone ?? 'neutral'}`}>{r.tag}</span>}
              </span>
            ) : (
              /* The name would be the result. A bar, not a blank and not a name. */
              <span className="dolwho">
                <span className="dolbadgehole" aria-hidden="true" />
                <span className="dolnamehole" aria-hidden="true" />
                <span className="dolvh">not drawn yet</span>
              </span>
            )}

            <span className="dolslotcell">
              {r.sealed ? (
                /* One element, so the bullet cannot wrap onto its own line above the word. */
                <em><span className="dolsealdot" aria-hidden="true" />sealed</em>
              ) : r.slot !== null ? (
                <>
                  <b>{r.slot}{ordinal(r.slot)}</b>
                  {r.slotNote && <i>{r.slotNote}</i>}
                </>
              ) : (
                <span className="dolslothole" aria-hidden="true" />
              )}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

/**
 * WHO HAS TURNED A RANKING IN — the right-hand edge of the GM cell, in every state but published.
 *
 * A TICK, NOT A WORD. Ten of these are read as a column, not as ten sentences: the question a GM
 * asks here is "is mine in", and the question a commissioner asks the night before the deadline
 * is "who is missing", and both are answered by scanning one 18px column. The words are on the
 * hover and in the accessible name, where they cost the 390px row nothing.
 *
 * THE STAMP IS THE POINT of the filled one. "Submitted" without a time is a claim; the tooltip
 * carries what the league actually recorded, in both zones, which is the same thing the GM's own
 * receipt card shows him — so the two can be read against each other in a dispute.
 *
 * AN UNSUBMITTED ROW IS A DASHED RING AND NOT A CROSS. §6 is not a failure: a GM who ranks
 * nothing takes the earliest slot left, which is the rule working. A red X on his row on the 29th
 * would be the screen telling nine people he is late for something that is optional until 5pm.
 */
function SubmittedMark({ row }: { row: LedgerRow }) {
  if (!row.submitted) return null;
  /*
   * A THIRD MARK FOR THE THIRD STATE. A half-filled dot for a list that exists and was never
   * sent. The resolve does not read it — he takes §6's earliest free slot like anybody who ranked
   * nothing — but a full ring would say he is done and an empty one would say he never wrote
   * anything, and he is neither. He is the one man on the board a message before the deadline
   * still moves.
   */
  const said = row.submitted === 'in' ? 'ranking submitted'
    : row.submitted === 'draft' ? 'ranking saved but not sent'
      : 'no ranking submitted yet';
  const why = row.submitted === 'in' && row.submittedAt
    ? `Ranking recorded ${bothZones(row.submittedAt)}`
    : row.submitted === 'draft'
      ? 'Saved but never sent, so the resolve will not read it — he takes the earliest slot left (§6).'
      : 'No ranking submitted yet — takes the earliest slot left (§6)';
  return (
    <span className={`dolsubmark ${row.submitted}`} title={why}>
      <span aria-hidden="true">{row.submitted === 'in' ? '\u2713' : ''}</span>
      <span className="dolvh">{said}</span>
    </span>
  );
}
